import { useState, Fragment, useEffect } from "react";
import { Dialog, Transition, Switch } from "@headlessui/react";
import { toastNotification } from "../../../../services/notification-service.js";
import { QuestionFormField } from "../../../jobs/jobQuestions/QuestionFormField.js";
import { McqOptions } from "../../../jobs/jobQuestions/McqOptions.js";

const TopicQuestionsModal = ({ topic, isOpen, onClose, onSave }) => {
  const [questions, setQuestions] = useState([]);
  const [imagePreviews, setImagePreviews] = useState({});
  const [openPublishModalIndex, setOpenPublishModalIndex] = useState(null);

  useEffect(() => {
    if (isOpen && topic?.additional_questions) {
      setQuestions(topic.additional_questions);
    } else if (isOpen) {
      setQuestions([]);
    }
  }, [isOpen, topic]);

const validateQuestions = () => {
  return questions.every((q) => {
    if (!q.question || !q.answer_type || !q.time_limit_seconds) return false;

    let options = q.options;
    if (typeof options === "string") {
      try {
        options = JSON.parse(options);
      } catch (err) {
        return false; 
      }
    }

    if (
      q.answer_type === "mcq" &&
      (!Array.isArray(options) || options.length < 2 || options.some((opt) => !opt))
    ) {
      return false;
    }

    if (
      q.answer_type !== "url" &&
      q.answer_type !== "varchar" &&
      !q.correct_answer
    ) {
      return false;
    }

    return true;
  });
};

  const addQuestion = () => {
    const newQuestion = {
      id: Date.now(),
      question: "",
      answer_type: "",
      options: null,
      correct_answer: "",
      time_limit_seconds: 30,
      has_image: false,
      image: null,
      imageName: "",
      is_justified: false,
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleQuestionChange = (index, e) => {
    const { name, value } = e.target;
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === index
          ? {
              ...q,
              [name]: value,
              ...(name === "answer_type" && value !== "mcq"
                ? { options: null }
                : {}),
              ...(name === "answer_type" && value === "boolean"
                ? { correct_answer: "true" }
                : {}),
              ...(name === "answer_type" && value !== "boolean"
                ? { correct_answer: "" }
                : {}),
            }
          : q
      )
    );
  };

const deleteQuestion = (index) => {
  const confirmDelete = window.confirm("Are you sure you want to delete this question?");
  
  if (confirmDelete) {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
    
    toastNotification({
      status: "success",
      message: "Question deleted successfully",
      duration: 3000,
    });
  }
};

  const handleSave = () => {
    if (!validateQuestions()) {
      toastNotification({
        status: "error",
        message: "Please fill in all required fields correctly.",
      });
      return;
    }

    onSave({
      ...topic,
      additional_questions: questions,
    });

    onClose();
    setQuestions([]);
    setImagePreviews({});
    toastNotification({
      status: "success",
      message: "Questions saved successfully",
    });
  };

  const renderTopicQuestions = () => {
    return (
      <div className="w-full mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Quiz for {topic.title}
          </h3>
          <button
            type="button"
            onClick={addQuestion}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Add Question
          </button>
        </div>

        {questions.length === 0 && (
          <div className="text-sm text-gray-500 mb-4">
            No questions added yet for this topic
          </div>
        )}

        {questions.map((question, index) => {
          const fullIndex = index;
          const showOptionsField = question.answer_type === "mcq";
          const showCorrectAnswerField = question.answer_type !== "url";
          const isURLType =
            question.answer_type === "url" ||
            question.answer_type === "varchar";

          return (
            <div
              key={fullIndex}
              className="grid grid-cols-12 w-full gap-6 mb-6 p-4 bg-gray-50 rounded-lg"
            >
              {[
                {
                  id: `question-${fullIndex}`,
                  name: "question",
                  type: "text",
                  value: question.question || "",
                  onChange: (e) => handleQuestionChange(fullIndex, e),
                  className:
                    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
                  placeholder: " ",
                  maxLength: 255,
                  required: true,
                  label: `Question ${fullIndex + 1}*`,
                  colSpan: "col-span-12 md:col-span-5",
                },
                {
                  id: `type-${fullIndex}`,
                  name: "answer_type",
                  type: "select",
                  value: question.answer_type || "",
                  onChange: (e) => handleQuestionChange(fullIndex, e),
                  className:
                    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
                  required: true,
                  label: "Type*",
                  options: [
                    { value: "", label: "Select Type" },
                    { value: "boolean", label: "True/False" },
                    { value: "varchar", label: "Short Answer" },
                    { value: "mcq", label: "Multiple Choice" },
                  ],
                  colSpan: "col-span-12 md:col-span-2",
                },
                {
                  id: `time-${fullIndex}`,
                  name: "time_limit_seconds",
                  type: "number",
                  value: question.time_limit_seconds || "",
                  onChange: (e) =>
                    handleQuestionChange(fullIndex, {
                      target: {
                        name: "time_limit_seconds",
                        value: e.target.value,
                      },
                    }),
                  onBlur: (e) => {
                    const value = Math.min(
                      600,
                      Math.max(5, parseInt(e.target.value)) || 5
                    );
                    handleQuestionChange(fullIndex, {
                      target: {
                        name: "time_limit_seconds",
                        value: isNaN(value) ? 5 : value,
                      },
                    });
                  },
                  className:
                    "block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer",
                  placeholder: " ",
                  min: 5,
                  max: 600,
                  required: true,
                  label: "Time Limit (5-600 sec)*",
                  colSpan: "col-span-12 md:col-span-2",
                },
              ].map((field, fieldIndex) => (
                <div key={fieldIndex} className={field.colSpan}>
                  <QuestionFormField {...field} />
                </div>
              ))}

              <div className="col-span-12 md:col-span-1 flex items-center justify-between">
                {/* <QuestionImageUpload
                  setData={setQuestions}
                  questionIndex={fullIndex}
                  hasImage={question.has_image}
                  setImagePreviews={setImagePreviews}
                  imagePreviews={imagePreviews}
                  question={question}
                /> */}

                {(question.answer_type === "boolean" ||
                  question.answer_type === "mcq") && (
                  <div className="flex items-center gap-2 ml-4">
                    <label
                      htmlFor={`is_justified-${fullIndex}`}
                      className="text-sm font-medium text-gray-700 whitespace-nowrap"
                    >
                      Justify
                    </label>
                    <Switch
                      id={`is_justified-${fullIndex}`}
                      name="is_justified"
                      checked={question.is_justified || false}
                      onChange={(enabled) =>
                        handleQuestionChange(fullIndex, {
                          target: { name: "is_justified", value: enabled },
                        })
                      }
                      className={`${
                        question.is_justified ? "bg-orange-600" : "bg-gray-200"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500`}
                    >
                      <span
                        className={`${
                          question.is_justified
                            ? "translate-x-6"
                            : "translate-x-1"
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </div>
                )}
              </div>

              <div className="col-span-12 md:col-span-2 flex items-center justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => deleteQuestion(fullIndex)}
                  className="text-red-600 hover:text-red-800"
                  aria-label="Delete question"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                </button>
              </div>

              {showOptionsField && (
                <McqOptions
                  questionIndex={fullIndex}
                  question={question}
                  onOptionsChange={(updatedFields) => {
                    setQuestions((prev) =>
                      prev.map((q, i) =>
                        i === fullIndex ? { ...q, ...updatedFields } : q
                      )
                    );
                  }}
                />
              )}

              {showCorrectAnswerField && (
                <div className="col-span-12 mt-4">
                  <QuestionFormField
                    id={`correct-${fullIndex}`}
                    name="correct_answer"
                    type={
                      question.answer_type === "boolean" ? "select" : "text"
                    }
                    value={question.correct_answer || ""}
                    onChange={(e) => handleQuestionChange(fullIndex, e)}
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-orange-600 peer"
                    placeholder=" "
                    required={!isURLType}
                    label={`Correct Answer${!isURLType ? "*" : ""}`}
                    options={
                      question.answer_type === "boolean"
                        ? [
                            { value: "true", label: "True" },
                            { value: "false", label: "False" },
                          ]
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="mt-4 max-h-[70vh] overflow-y-auto">
                  {renderTopicQuestions()}
                </div>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                    onClick={() => {
                      onClose();
                      setQuestions([]);
                      setImagePreviews({});
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
                    onClick={handleSave}
                  >
                    Save Changes
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TopicQuestionsModal;
